// Authentik OAuth2 Configuration

export interface AuthentikConfig {
  baseUrl: string;        // Base Authentik URL (e.g., https://auth.trawowa.eu)
  appSlug: string;        // Application slug (e.g., marmo-info-car)
  issuer: string;         // Full issuer URL
  clientId: string;
  redirectUri: string;
  scope: string;
  // Endpoints
  authorizeEndpoint: string;
  tokenEndpoint: string;
  jwksEndpoint: string;
}

export const getAuthentikConfig = (): AuthentikConfig => {
  const baseUrl = import.meta.env.VITE_AUTHENTIK_BASE_URL || 'https://auth.trawowa.eu';
  const appSlug = import.meta.env.VITE_AUTHENTIK_APP_SLUG || 'marmo-info-car';
  const clientId = import.meta.env.VITE_AUTHENTIK_CLIENT_ID || '';

  return {
    baseUrl,
    appSlug,
    issuer: `${baseUrl}/application/o/${appSlug}/`,
    clientId,
    redirectUri: `${window.location.origin}/auth/callback`,
    scope: 'openid profile email',
    // Authentik uses generic endpoints for authorize/token
    authorizeEndpoint: `${baseUrl}/application/o/authorize/`,
    tokenEndpoint: `${baseUrl}/application/o/token/`,
    // JWKS is app-specific
    jwksEndpoint: `${baseUrl}/application/o/${appSlug}/jwks/`,
  };
};

/**
 * Generate a random state parameter for CSRF protection
 */
export function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate PKCE code verifier
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate PKCE code challenge from verifier
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
}

function base64UrlEncode(array: Uint8Array): string {
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Build Authentik authorization URL
 */
export async function buildAuthUrl(): Promise<{ url: string; state: string; codeVerifier: string }> {
  const config = getAuthentikConfig();
  const state = generateState();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Store state and verifier for validation
  sessionStorage.setItem('oauth_state', state);
  sessionStorage.setItem('oauth_code_verifier', codeVerifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  return {
    url: `${config.authorizeEndpoint}?${params.toString()}`,
    state,
    codeVerifier,
  };
}

/**
 * Exchange authorization code for tokens
 */
export async function exchangeCodeForTokens(code: string): Promise<{
  id_token: string;
  access_token: string;
  refresh_token?: string;
}> {
  const config = getAuthentikConfig();
  const codeVerifier = sessionStorage.getItem('oauth_code_verifier');

  if (!codeVerifier) {
    throw new Error('Code verifier not found');
  }

  const response = await fetch(config.tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      code_verifier: codeVerifier,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token exchange failed: ${error}`);
  }

  // Clean up stored values
  sessionStorage.removeItem('oauth_state');
  sessionStorage.removeItem('oauth_code_verifier');

  return response.json();
}

/**
 * Parse JWT token payload
 */
export function parseJwt(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload);
}
